import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { useContext, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { DatadogMonitorSearch } from 'components/status-checks/form/status-check/datadog/DatadogMonitorSearch'
import { DatadogMonitorURL } from 'components/status-checks/form/status-check/datadog/DatadogMonitorURL'
import {
  StatusCheckFormValues,
  StatusCheckMonitorSelectionRadioValue
} from 'components/status-checks/status-checks-form-utils'
import { StatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { useTranslation } from 'hooks/use-translation'
import { DATADOG_MONITOR_URL_PREFIX } from 'utils/status-checks/status-check-integrations-util-datadog'

export const DatadogMonitorSelection: React.FC = () => {
  const { trans } = useTranslation()
  const { serviceName } = useContext(StatusCheckDetailsContext)

  const { control, formState, setValue, trigger } =
    useFormContext<StatusCheckFormValues>()

  const [domain, externalIntegration] = useWatch<StatusCheckFormValues>({
    control,
    name: ['domain', 'externalIntegration']
  })

  const [radioValue, setRadioValue] =
    useState<StatusCheckMonitorSelectionRadioValue>(
      serviceName
        ? StatusCheckMonitorSelectionRadioValue.SEARCH
        : StatusCheckMonitorSelectionRadioValue.URL
    )

  return (
    <Stack mb={2} spacing={3}>
      <FormControl>
        <Typography mb={2}>
          {trans(
            'forms.save_status_check.connect_monitor.status_check_name.prompt'
          )}
        </Typography>
        <Controller
          control={control}
          name="statusCheckName"
          render={({ field }) => (
            <TextField
              {...field}
              data-testid="status-check-monitor-selection-name-input"
              error={Boolean(formState.errors.statusCheckName)}
              helperText={formState.errors.statusCheckName?.message}
              label={trans(
                'forms.save_status_check.connect_monitor.status_check_name.text_field.label'
              )}
              variant="outlined"
            />
          )}
        />
      </FormControl>
      <Typography>
        {trans('forms.save_status_check.connect_monitor.monitor_url.prompt')}
      </Typography>
      <RadioGroup
        name="monitor-selection"
        value={radioValue}
        onChange={(_, value: StatusCheckMonitorSelectionRadioValue) =>
          setRadioValue(value)
        }
      >
        <Stack direction="column" ml={4} rowGap={2}>
          <FormControl>
            <FormControlLabel
              control={<Radio />}
              label={trans(
                'forms.save_status_check.connect_monitor.monitor_url.radio.label'
              )}
              value={StatusCheckMonitorSelectionRadioValue.URL}
            />
            <Box ml={4}>
              <DatadogMonitorURL radioValue={radioValue} />
            </Box>
          </FormControl>
          <FormControl>
            <FormControlLabel
              control={<Radio />}
              label={trans(
                'forms.save_status_check.connect_monitor.monitor_search.radio.label'
              )}
              value={StatusCheckMonitorSelectionRadioValue.SEARCH}
            />
            <Box ml={4}>
              <DatadogMonitorSearch
                disabled={
                  radioValue !== StatusCheckMonitorSelectionRadioValue.SEARCH
                }
                domain={domain?.name}
                initialSearchValue={serviceName}
                integrationName={externalIntegration.name}
                onSelected={async (newValue) => {
                  setValue(
                    'monitorUrl',
                    `${externalIntegration.url}${DATADOG_MONITOR_URL_PREFIX}${newValue}`,
                    { shouldDirty: true } // Required to advance through edit workflow
                  )
                  await trigger('monitorUrl')
                }}
              />
            </Box>
          </FormControl>
        </Stack>
      </RadioGroup>
    </Stack>
  )
}
